import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Brain, Send, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { EmailComposer, LenderSubmissionModal } from '../EmailComposer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function NotesTab({ deal, dealId, onRefresh }) {
  const activities = deal.activities || [];
  const [newNote, setNewNote] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showLenderSubmit, setShowLenderSubmit] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/crm/activities`, { deal_id: dealId, activity_type: 'note', description: newNote });
      setNewNote(''); onRefresh(); toast.success('Note added');
    } catch (e) { toast.error('Failed to add note'); }
  };

  const handleAI = async (action) => {
    setAiLoading(true);
    try {
      await axios.post(`${API}/crm/ai-notes`, { deal_id: dealId, action, notes_text: newNote || undefined });
      toast.success(action === 'analyze' ? 'AI analysis generated' : 'Submission summary generated');
      onRefresh();
    } catch (e) { toast.error('AI analysis failed'); }
    finally { setAiLoading(false); }
  };

  const typeLabels = {
    note: 'Note', stage_change: 'Stage', email_sent: 'Email', document_uploaded: 'Doc',
    task_completed: 'Task', lender_submission: 'Lender', ai_analysis: 'AI', ai_submission_summary: 'AI Summary',
  };
  const typeColors = {
    note: 'bg-gray-500/20 text-gray-400', stage_change: 'bg-blue-500/20 text-blue-400',
    email_sent: 'bg-green-500/20 text-green-400', document_uploaded: 'bg-cyan-500/20 text-cyan-400',
    task_completed: 'bg-emerald-500/20 text-emerald-400', lender_submission: 'bg-purple-500/20 text-purple-400',
    ai_analysis: 'bg-purple-500/20 text-purple-400', ai_submission_summary: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="notes-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2"><MessageSquare className="h-5 w-5 text-yellow-400" /> Notes & Emails</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowEmail(true)} size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="compose-email-btn">
            <Send className="h-4 w-4 mr-1" />Email
          </Button>
          <Button onClick={() => setShowLenderSubmit(true)} size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="lender-submit-btn">
            Submit to Lender
          </Button>
        </div>
      </div>

      {/* Add note */}
      <div className="space-y-2">
        <textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={3} placeholder="Add a note..."
          className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none resize-none" />
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button onClick={() => handleAI('analyze')} disabled={aiLoading} size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
              {aiLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1" />} AI Analyse
            </Button>
            <Button onClick={() => handleAI('submission_summary')} disabled={aiLoading} size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
              Submission Summary
            </Button>
          </div>
          <Button onClick={handleAddNote} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-note-btn">Add Note</Button>
        </div>
      </div>

      {/* Activity timeline */}
      <div className="space-y-2">
        {activities.map(a => (
          <div key={a.id} className="flex gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`activity-${a.id}`}>
            <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${typeColors[a.activity_type] || 'bg-gray-500/20 text-gray-400'}`}>
              {typeLabels[a.activity_type] || 'Note'}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-300 whitespace-pre-wrap break-words">{a.description}</div>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(a.created_at).toLocaleString()} &middot; {a.created_by || 'user'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {showEmail && <EmailComposer deal={deal} onClose={() => setShowEmail(false)} onSent={onRefresh} />}
      {showLenderSubmit && <LenderSubmissionModal deal={deal} onClose={() => setShowLenderSubmit(false)} onSubmitted={onRefresh} />}
    </div>
  );
}
